/**
 * PEG-546: Route guard for admin-only routes.
 *
 * Routes (or any ancestor) flagged with `meta.requiresAdmin: true`
 * are gated behind a valid internal admin session (PEG-509 token-
 * exchange). Retailer sessions are silently redirected to the retailer
 * dashboard home — we deliberately do NOT surface that an admin-only
 * surface exists (per AC: "any indication that the claims management
 * feature exists" is hidden from retailer sessions).
 *
 * Server-side enforcement also rejects non-admin sessions for the
 * underlying APIs; this guard is the UX half of the gate.
 *
 * Note: the admin marker cookie is set during PEG-509 launch. We
 * trigger a session fetch the first time we see the marker but no
 * session yet, then re-check.
 */
import { useAdminStore } from '@/stores/admin.store'

function routeRequiresAdmin(route) {
  return route.matched.some(record => record.meta?.requiresAdmin)
}

export async function applyAdminRouteGuard(to) {
  if (!routeRequiresAdmin(to)) {
    return
  }
  const adminStore = useAdminStore()
  // If we haven't checked yet but the same-domain marker cookie says
  // there might be an admin session, fetch it once.
  if (!adminStore.checked && adminStore.hasAdminMarker()) {
    try {
      await adminStore.fetchSession()
    } catch {
      // best-effort — fall through to the isAdminMode check below
    }
  }
  if (adminStore.isAdminMode) {
    return
  }
  // Silently redirect retailer sessions to home; no claim that the
  // claims surface exists.
  return { name: 'RetailerHome' }
}
